import styled from 'styled-components';

// Layout flat — cada KPI vira uma celula bordered (hairline var(--cco-line)
// + radius 12) com padding interno. Sem mais border-left compartilhada
// entre celulas — match exato do design (cards independentes separados
// por gap, nao por divisor interno).
//
// Container nao tem padding lateral pq a Section pai (em styled-appliance)
// ja aplica padding var(--cco-pad). Aqui so o stack vertical entre TopRow
// e PeriodRow (gap 12).
export const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0;
  box-sizing: border-box;
  min-height: 0;
`;

// Linha de cima: PrimaryKPI (Total Energia, 1.4x) + 3 SecondaryKPIs
// (Potencia, Eficiencia, Alarme/Erro). UMA caixa externa bordered
// envolvendo as 4 celulas, divididas por hairline vertical interno
// (nao gap). Aproveita o espaco e da o visual do design Plant.html.
export const TopRow = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 1.4fr) repeat(3, minmax(0, 1fr));
  align-items: stretch;
  border: 1px solid var(--cco-line);
  border-radius: 12px;
  overflow: hidden;

  /* Hairline interna entre celulas. > * + * pega da 2a em diante,
     ent so as divisoes ficam riscadas (nao a borda externa). */
  > * + * {
    border-left: 1px solid var(--cco-line);
  }

  /* BLUEBOX tem 5 KPIs (com Horimetro) — no grid de 4 colunas o 5o caia
     sozinho embaixo. Aqui: primario alto a esquerda + secundarios em 2x2
     (Potencia/FP em cima, Alarme/Horimetro embaixo). So em telas largas;
     no mobile cai pro empilhamento 2-col padrao abaixo. */
  ${(props) =>
    props.$bluebox &&
    `
    @media (min-width: 1101px) {
      grid-template-columns: minmax(180px, 1.4fr) repeat(2, minmax(0, 1fr));
      > *:nth-child(1) {
        grid-row: 1 / span 2;
      }
      > *:nth-child(4),
      > *:nth-child(5) {
        border-top: 1px solid var(--cco-line);
      }
    }
  `}

  @media (max-width: 1100px) {
    grid-template-columns: 1fr 1fr;
    > * + * {
      border-left: none;
      border-top: 1px solid var(--cco-line);
    }
  }
`;

// Esqueleto base de cada celula KPI dentro da row. Sem borda propria,
// sem radius — a row pai e que tem essas propriedades. Padding interno
// pra respiro e flex column pra label+valor empilhados.
const kpiCellBase = `
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  padding: 14px 16px;
  background: transparent;
  box-sizing: border-box;
  min-width: 0;
`;

// PrimaryKPI — Total Energia Gerada. Sem borda propria (a TopRow tem),
// so o accent stripe vertical na esquerda (orange UCHA / blue EBA).
export const PrimaryKPI = styled.div`
  ${kpiCellBase}
  position: relative;
  padding-left: 18px;

  ::before {
    content: '';
    position: absolute;
    left: 0;
    top: 14px;
    bottom: 14px;
    width: 3px;
    border-radius: 3px;
    background: ${(props) =>
      props.accent === 'eba' ? 'var(--cco-blue)' : 'var(--cco-orange)'};
  }
`;

export const PrimaryLabel = styled.span`
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-weight: 600;
  color: var(--cco-ink-mute);
  line-height: 1;
`;

export const PrimaryValue = styled.div`
  font-size: clamp(22px, 2.2vw, 30px);
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  line-height: 1.05;
  color: ${(props) => (props.color ? props.color : 'var(--cco-ink)')};
  word-break: break-word;

  small {
    font-size: 0.45em;
    font-weight: 600;
    color: var(--cco-ink-mute);
    margin-left: 4px;
  }
`;

export const PrimaryMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
`;

export const PrimaryMetaLabel = styled.span`
  font-size: 9.5px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-weight: 600;
  color: var(--cco-ink-mute);
`;

// SecondaryKPI — Maquinas, Ontime, Eficiencia, Alarme/Erro. Mesma
// celula bordered, sem accent lateral (so o primario tem stripe).
export const SecondaryKPI = styled.div`
  ${kpiCellBase}
`;

export const KPILabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-weight: 600;
  color: var(--cco-ink-mute);
  line-height: 1;
`;

export const KPIValue = styled.div`
  font-size: clamp(18px, 1.8vw, 24px);
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  line-height: 1.05;
  color: ${(props) => (props.color ? props.color : 'var(--cco-ink)')};
  word-break: break-word;

  small {
    font-size: 0.5em;
    font-weight: 600;
    color: var(--cco-ink-mute);
    margin-left: 4px;
  }
`;

export const KPISubValue = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: var(--cco-ink-mute);
  text-transform: uppercase;
  letter-spacing: 0.10em;
  cursor: help;
  line-height: 1.2;
`;

// Divider entre TopRow e PeriodRow — nao mais necessario com celulas
// bordered (cada bloco ja se destaca). Mantido como no-op pra nao
// quebrar import; renderiza vazio (zero height).
export const Divider = styled.div`
  display: none;
`;

// PeriodRow — "ENERGIA GERADA POR PERÍODO" label cell + 3 period cells
// (Mes/Semana/Dia). Mesmo pattern da TopRow: caixa externa unica
// bordered com hairlines internas dividindo as 4 celulas.
export const PeriodRow = styled.div`
  display: grid;
  grid-template-columns: minmax(160px, 1fr) repeat(3, minmax(0, 1.2fr));
  align-items: stretch;
  border: 1px solid var(--cco-line);
  border-radius: 12px;
  overflow: hidden;

  > * + * {
    border-left: 1px solid var(--cco-line);
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
    > * + * {
      border-left: none;
      border-top: 1px solid var(--cco-line);
    }
  }
`;

// Celula do label "ENERGIA GERADA POR PERÍODO". Sem borda propria,
// inherente da PeriodRow.
export const PeriodLabelCell = styled.div`
  ${kpiCellBase}
  justify-content: center;
`;

export const PeriodSectionLabel = styled.span`
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-weight: 600;
  color: var(--cco-ink-mute);
  line-height: 1.3;
`;

export const PeriodCell = styled.div`
  ${kpiCellBase}
  gap: 4px;
`;

export const PeriodLabel = styled.span`
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-weight: 600;
  color: var(--cco-ink-mute);
  line-height: 1;
`;

export const PeriodValue = styled.span`
  font-size: clamp(16px, 1.5vw, 20px);
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
  color: ${(props) => (props.negative ? 'var(--cco-red)' : 'var(--cco-ink)')};

  small {
    font-size: 0.55em;
    font-weight: 600;
    color: var(--cco-ink-mute);
    margin-left: 4px;
  }
`;

// AlarmList — popup do Floater (continua igual, ja era discreto).
export const AlarmList = styled.div`
  background: var(--cco-bg);
  border: 1px solid var(--cco-line);
  border-radius: 12px;
  max-height: 280px;
  overflow-y: auto;
`;

export const AlarmListTitle = styled.div`
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--cco-red);
  padding: 10px 14px 6px;
  border-bottom: 1px solid var(--cco-line);
`;

export const AlarmListItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 14px;
  border-bottom: 1px solid var(--cco-line);

  :last-child {
    border-bottom: none;
  }
`;

export const AlarmListMachine = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: var(--cco-ink);
`;

export const AlarmListVars = styled.div`
  font-size: 11px;
  color: var(--cco-ink-mute);
  font-family: 'JetBrains Mono', 'Courier New', monospace;
  word-break: break-word;
`;

// Sparkline wrapper — fica abaixo do PrimaryValue dentro da celula.
export const PrimarySparklineWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  margin-top: 4px;
`;

// Botao info (?) circular — mesmo padrao do antigo, reutilizado.
export const Info = styled.div`
  display: inline-flex;
  border-radius: 50%;
  cursor: help;
  background: var(--cco-ink-mute);
  color: var(--cco-bg);
  width: 13px;
  height: 13px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 900;
  transition: background 150ms ease;

  :hover {
    background: var(--cco-blue);
  }

  ::before {
    content: '?';
    line-height: 1;
  }
`;
